import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';

import {
  HubTheme,
  HubThemeResponse,
  StreaksTheme,
  StreaksThemeResponse,
  WheelTheme,
  WheelThemeResponse,
} from '../models/theme.type';

import { GameCampaign } from '../models/campaign.type';

const STREAKS_FALLBACK_URL = 'assets/themes/streaks-fallback.json';
const WHEEL_FALLBACK_URL = 'assets/themes/wheel-fallback.json';
const HUB_FALLBACK_URL = 'assets/themes/hub-fallback.json';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly http = inject(HttpClient);
  private readonly httpBackend = inject(HttpBackend);
  private rawHttp = new HttpClient(this.httpBackend);

  private readonly _streaksTheme$ = new BehaviorSubject<StreaksTheme | null>(
    null,
  );
  readonly streaksTheme$ = this._streaksTheme$.asObservable();

  private readonly _wheelTheme$ = new BehaviorSubject<WheelTheme | null>(null);
  readonly wheelTheme$ = this._wheelTheme$.asObservable();

  private readonly _hubTheme$ = new BehaviorSubject<HubTheme | null>(null);
  readonly hubTheme$ = this._hubTheme$.asObservable();

  private readonly _campaign$ = new BehaviorSubject<GameCampaign | null>(null);
  readonly campaign$ = this._campaign$.asObservable();

  loadThemes(config: {
    streaksThemeUrl?: string | null;
    wheelThemeUrl?: string | null;
    hubThemeUrl?: string | null;
  }) {
    return forkJoin({
      streaks: this.loadStreaksTheme(config.streaksThemeUrl),
      wheel: this.loadWheelTheme(config.wheelThemeUrl),
      hub: this.loadHubTheme(config.hubThemeUrl),
    }).pipe(
      tap(({ streaks, wheel, hub }) => {
        if (streaks) {
          this.applyStreaksTheme(streaks.theme);
          this._streaksTheme$.next(streaks.theme);
        }

        if (wheel) {
          this._wheelTheme$.next(wheel.theme);
        }

        if (hub) {
          this.applyHubTheme(hub.theme);
          this._hubTheme$.next(hub.theme);

          if (hub.campaign) {
            this._campaign$.next(hub.campaign);
          }
        }
      }),
    );
  }

  private loadStreaksTheme(
    url?: string | null,
  ): Observable<StreaksThemeResponse | null> {
    return this.rawHttp
      .get<StreaksThemeResponse>(url || STREAKS_FALLBACK_URL)
      .pipe(
        take(1),
        catchError((err) => {
          console.warn('[Theme - streaks] Failed to load theme: ', err);
          if (url && url !== STREAKS_FALLBACK_URL) {
            console.warn('[Theme - streaks] loading fallback theme');
            return this.http.get<StreaksThemeResponse>(STREAKS_FALLBACK_URL);
          }

          return of(null);
        }),
      );
  }

  private loadWheelTheme(
    url?: string | null,
  ): Observable<WheelThemeResponse | null> {
    return this.rawHttp.get<WheelThemeResponse>(url || WHEEL_FALLBACK_URL).pipe(
      take(1),
      catchError((err) => {
        console.warn('[Theme - wheel] Failed to load theme: ', err);
        if (url && url !== WHEEL_FALLBACK_URL) {
          console.warn('[Theme - wheel] loading fallback theme');
          return this.http.get<WheelThemeResponse>(WHEEL_FALLBACK_URL);
        }

        return of(null);
      }),
    );
  }

  private loadHubTheme(
    url?: string | null,
  ): Observable<HubThemeResponse | null> {
    return this.rawHttp.get<HubThemeResponse>(url || HUB_FALLBACK_URL).pipe(
      take(1),
      catchError((err) => {
        console.warn('[Theme - hub] Failed to load theme: ', err);
        if (url && url !== HUB_FALLBACK_URL) {
          console.warn('[Theme - hub] loading fallback theme');
          return this.http.get<HubThemeResponse>(HUB_FALLBACK_URL);
        }

        return of(null);
      }),
    );
  }

  private applyStreaksTheme(theme: StreaksTheme): void {
    const root = document.documentElement;

    for (const { key, value } of theme.cssVariables) {
      if (value != null) {
        root.style.setProperty(key, value);
      }
    }
  }

  private applyHubTheme(theme: HubTheme): void {
    const root = document.documentElement;

    root.style.setProperty(
      '--block-border-radius',
      `${theme.blocksBorderRadius ?? 24}px`,
    );
  }

  label(key: string, fallback = ''): string {
    const labels = this._streaksTheme$.value?.labels;
    if (!Array.isArray(labels) || !labels.length) return fallback;
    return labels.find((l) => l.key === key)?.value ?? fallback;
  }
}
